import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import DigitisationService from "./DigitisationService";
import { LI_V2_EDIT, LI_V2_REFRESH } from "./events";
import { CheckGlyph, CloseGlyph, SparkleGlyph } from "./labIcons";
import ModalPortal from "../common/ModalPortal";

/**
 * Fixed Document ID sentinels — appended to the fetched document list so
 * the reviewer can capture a lab conversation even when no document is
 * attached. These are pure UI strings; the backend sees them as-is (no
 * hard-coded parsing required — a Document ID like "user-informed-via-app"
 * is a valid string tag).
 */
export const SENTINEL_DOC_IDS = [
  { value: "user-informed-verbally", label: "User informed verbally" },
  { value: "user-informed-via-app",  label: "User informed via app" },
  { value: "report-shared-by-client", label: "Report shared by client" },
  { value: "none",                   label: "None" },
];
const SENTINEL_SET = new Set(SENTINEL_DOC_IDS.map((s) => s.value));
export const isSentinelDocId = (id) => SENTINEL_SET.has(String(id || ""));

/**
 * "Digitise report" modal — 2-stage: pick a document, kick off OCR, show
 * the success card. Digitisation runs async upstream (bridge's copy says
 * "check back in 10 minutes"), so we don't wait for parsed rows — the
 * extracted values land in the section on the next refresh. The client-
 * side review step the earlier three-stage build carried was dropped
 * because the actual upstream endpoint (`POST /v1/lab-report-digitisation
 * /digitise-report`) is a trigger, not a synchronous parse. See bridge-
 * frontend/src/modules/misc/modules/document-digitisation for the same
 * flow in the legacy dashboard.
 *
 * Stages:
 *   pick     → dropdown of documents (populated from the backend proxy of
 *              `GET /v1/documents/digitisation/:patientId`), "View
 *              document" opens the doc URL in a new tab, "Digitise now"
 *              disabled until a doc is picked.
 *   done     → green medallion + success meta + "Done".
 *
 * On success the section list is refreshed (`LI_V2_REFRESH`) so newly-
 * persisted rows show as they arrive.
 */
const DigitiseModal = ({ patientId, appointmentId, docVersion, onClose }) => {
  const [stage, setStage] = useState("pick"); // "pick" | "done"
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState("");
  const [digitising, setDigitising] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [result, setResult] = useState(null); // { documentId, count, message }
  // Pre-fetched document-viewer token — bridge fires this on mount so the
  // "View document" window.open runs synchronously in the click handler
  // (async token fetches inside the handler get blocked by popup blockers).
  const [docToken, setDocToken] = useState("");

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !digitising && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, digitising]);

  useEffect(() => {
    const svc = new DigitisationService();
    svc.listDocuments(
      patientId,
      (data) => {
        if (!mounted.current) return;
        setDocs(Array.isArray(data) ? data : []);
        setDocsLoading(false);
      },
      () => {
        if (!mounted.current) return;
        setDocs([]);
        setDocsLoading(false);
      }
    );
    // Warm the doc-viewer token in parallel — cheap, cached in
    // localStorage under bridge's `zy.bridge.doc.token` so second-open
    // hits localStorage. Errors are non-fatal.
    svc.getDocumentToken(
      (t) => { if (mounted.current) setDocToken(t || ""); },
      () => {}
    );
  }, [patientId]);

  const selectedDocObj = docs.find((d) => String(d.id) === String(selectedDoc));

  const runDigitise = () => {
    if (!selectedDoc || digitising) return;
    setDigitising(true);
    setFeedback(null);
    const svc = new DigitisationService();
    svc.digitise(
      patientId,
      selectedDoc,
      (res) => {
        if (!mounted.current) return;
        setDigitising(false);
        if (res && res.success === false) {
          setFeedback({
            kind: "err",
            text: (res && res.message) || "Digitisation failed — please retry",
          });
          return;
        }
        setResult({
          documentId: (res && res.documentId) || selectedDoc,
          count: res && typeof res.count === "number" ? res.count : null,
          message: res && res.message,
        });
        setStage("done");
        // Async upstream; refresh the section anyway so any values that
        // land quickly show up without a manual reload.
        EventService.emit(LI_V2_REFRESH);
      },
      (err) => {
        if (!mounted.current) return;
        setDigitising(false);
        setFeedback({
          kind: "err",
          text: (err && err.message) || "Digitisation failed — please retry",
        });
      }
    );
  };

  // The file host returns 403 on direct hits; append the pre-fetched
  // signed token as `?token=…`. Kept synchronous so the popup blocker
  // treats this as a direct user gesture — an async fetch inside the
  // handler would break that heuristic.
  const viewDocument = () => {
    if (!selectedDocObj || !selectedDocObj.url) return;
    const raw = selectedDocObj.url;
    const sep = raw.includes("?") ? "&" : "?";
    const url = docToken ? `${raw}${sep}token=${encodeURIComponent(docToken)}` : raw;
    window.open(url, "_blank", "noopener");
  };

  // A sentinel Doc ID represents a self-reported / no-document capture,
  // so the OCR + view-document paths don't apply. The Digitise-manually
  // path stays open regardless — that's how the reviewer types values in.
  const isSentinel = isSentinelDocId(selectedDoc);
  const canDigitise = !!selectedDoc && !isSentinel && !docsLoading && !digitising;
  const canView = !!selectedDocObj && !!selectedDocObj.url && !isSentinel && !digitising;
  const canManual = !!selectedDoc && !digitising;

  return (
    <ModalPortal>
      <div className="li-dg-backdrop" onMouseDown={digitising ? null : onClose}>
        <div
          className={`li-dg-modal is-${stage}`}
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Digitise report"
        >
          {stage === "pick" && (
            <>
              <header className="li-dg-head">
                <div className="li-dg-titles">
                  <h2 className="li-dg-title">Digitise report</h2>
                  <p className="li-dg-sub">
                    We&rsquo;ll read the document and pull values into the right panels.
                  </p>
                </div>
                <button
                  type="button"
                  className="li-dg-close"
                  onClick={digitising ? null : onClose}
                  disabled={digitising}
                  aria-label="Close"
                >
                  <CloseGlyph />
                </button>
              </header>
  
              <div className="li-dg-body">
                <div className="li-dg-fields">
                  <div className="li-dg-field">
                    <label className="li-dg-lbl" htmlFor="li-patient-id">Patient ID</label>
                    <input
                      id="li-patient-id"
                      className="li-dg-input is-readonly"
                      type="text"
                      value={patientId}
                      readOnly
                    />
                  </div>
                  <div className="li-dg-field">
                    <label className="li-dg-lbl" htmlFor="li-doc-id">
                      Document ID <span className="li-dg-req">*</span>
                    </label>
                    <div className="li-dg-select-wrap">
                      <select
                        id="li-doc-id"
                        className={`li-dg-input is-select${selectedDoc ? "" : " is-placeholder"}`}
                        value={selectedDoc}
                        onChange={(e) => setSelectedDoc(e.target.value)}
                        disabled={docsLoading || digitising}
                      >
                        <option value="">Select document ID</option>
                        {docsLoading && (
                          <option value="" disabled>Loading documents…</option>
                        )}
                        {docs.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.id}
                            {d.name ? ` — ${d.name}` : ""}
                          </option>
                        ))}
                        {/* Always-available fallbacks so a manual capture
                            can proceed without an attached document. */}
                        {SENTINEL_DOC_IDS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                      <span className="li-dg-chev" aria-hidden="true">&#8964;</span>
                    </div>
                  </div>
                </div>
  
                <p className="li-dg-ai">
                  <span className="li-dg-ai-icon"><SparkleGlyph /></span>
                  Values are AI-extracted and shown for your review before anything is saved.
                </p>
  
                {feedback && <div className={`li-dg-feedback is-${feedback.kind}`}>{feedback.text}</div>}
              </div>
  
              <footer className="li-dg-foot">
                <button
                  type="button"
                  className="li-dg-ghost"
                  onClick={viewDocument}
                  disabled={!canView}
                  title={
                    !selectedDoc
                      ? "Select a document first"
                      : isSentinel
                      ? "No document attached for this selection"
                      : canView
                      ? undefined
                      : "This document has no viewable link"
                  }
                >
                  View document
                </button>
                <button
                  type="button"
                  className="li-dg-ghost"
                  onClick={() => {
                    if (!canManual) return;
                    // Route the manual-entry step into the LI editor
                    // rail as a DigitiseManuallyCard instead of stacking
                    // a second centred modal. LabInvestigationEditor
                    // listens on LI_V2_EDIT and branches on the
                    // __digitiseManual marker to render the card,
                    // pre-seeded with the doc the reviewer picked here.
                    EventService.emit(LI_V2_EDIT, {
                      __digitiseManual: true,
                      docId:      selectedDoc,
                      documents:  docs,
                      docToken,
                      docVersion: typeof docVersion === "number" ? docVersion : 0,
                    });
                    onClose();
                  }}
                  disabled={!canManual}
                  title={!selectedDoc ? "Select a document first" : undefined}
                >
                  Digitise manually
                </button>
                <button
                  type="button"
                  className="li-dg-primary"
                  onClick={runDigitise}
                  disabled={!canDigitise}
                  title={
                    isSentinel
                      ? "No document to OCR — use Digitise manually"
                      : !selectedDoc && !docsLoading
                      ? "Select a document first"
                      : undefined
                  }
                >
                  <span className="li-dg-primary-icon"><SparkleGlyph /></span>
                  {digitising ? "Digitising…" : "Digitise now"}
                </button>
              </footer>
            </>
          )}
  
          {stage === "done" && result && (
            <div className="li-dg-done">
              <span className="li-dg-done-medallion" aria-hidden="true"><CheckGlyph /></span>
              <p className="li-dg-done-title">Lab investigation digitised successfully</p>
              <p className="li-dg-done-meta">
                Document {result.documentId}
                {typeof result.count === "number" && (
                  <> &middot; {result.count} value{result.count === 1 ? "" : "s"} added to Lab investigations</>
                )}
                {typeof result.count !== "number" && (
                  <> &middot; extracted values will appear shortly</>
                )}
              </p>
              <p className="li-dg-done-caption">AI-extracted values were reviewed before saving.</p>
              <button type="button" className="li-dg-done-btn" onClick={onClose}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

DigitiseModal.propTypes = {
  patientId: PropTypes.number.isRequired,
  /** Currently-selected appointment on the patient screen; forwarded
   *  onto DigitiseManuallyCard so every batch-persisted reading is
   *  stamped with it. */
  appointmentId: PropTypes.number,
  docVersion: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};

export default DigitiseModal;
